import { el, setAttr } from 'redom';
import { debounce } from 'lodash';

import OutsideClickEventRegistration from 'lib/outside-click-event-registration';
import { getActiveValue, getSelectedEntries } from './utilities';

import ActionsAndOptionsList from './actions-and-options-list';
import OptionsPositioner from '../options-positioner';

import keys from 'constants/keys';

class SingleValueSelector {

  constructor(options) {

    this.options = options.options;
    this.values = getSelectedEntries(options.value);
    this.placeholder = options.placeholder;
    this.optionsLoader = options.optionsLoader;
    this.updateOptions = debounce(this.updateOptions.bind(this), 250);
    this.OptionEntry = options.OptionEntry;
    this.actions = options.actions;
    this.onBlur = options.onBlur || function onBlur() {};
    this.addNewOption = options.addNewOption || false;

    this.onSelect = options.onSelect;
    this.onClose = options.onClose;
    this.skipPositioning = options.skipPositioning;
    this.skipScrollingToFocus = options.skipScrollingToFocus;
    this.onContentChanged = options.onContentChanged || function noop() {};

    this.input = null;
    this.optionsList = null;

    this.initializeInput();
    this.initializeOptionsList();

    this.el = el('.single-value-selector-main', [this.input, this.optionsList]);

    this.initializePositioner();

  }

  onmount() {

    if (this.options.length === 0) {
      this.refreshOptions();
    } else {
      this.optionsList.setEntries(this.options);
    }
    this.registerOutsideEventHandler();
    this.positioner.start();
    this.showOptions();
    this.notifyContentChanged();

  }

  onunmount() {

    this.unregisterOutsideEventHandler();
    this.positioner.stop();
    this.optionsList.setEntries([]);

  }

  initializePositioner() {

    this.positioner = new OptionsPositioner(this.el, this.input, this.optionsList.el);

    if (this.skipPositioning === true) {
      this.positioner.disable();
    }

  }

  showOptions() {

    this.positioner.applyPosition();
    this.positioner.scheduleReposition();

  }

  initializeInput() {

    const value = getActiveValue(this.values);
    this.input = el('input.single-value-selector-input', {
      type: 'text',
      placeholder: this.placeholder,
      value: value.label,
      oninput: this.handleTextInputChange.bind(this),
      onkeydown: this.handleTextInputKeyDownEvent.bind(this),
      onblur: this.handleBlurEvent.bind(this),
      onfocus: this.showOptions.bind(this)
    });

    this.input.previousValue = '';

  }

  initializeOptionsList() {

    const options = {
      entries: this.options,
      selectedEntries: this.values,
      onSelect: this.handleValueSelect.bind(this),
      onClose() {},
      scrollAnchorElement: this.input,
      skipPositioning: this.skipPositioning,
      OptionEntry: this.OptionEntry,
      actions: this.actions,
      skipScrollingToFocus: this.skipScrollingToFocus
    };

    this.optionsList = new ActionsAndOptionsList(options);

    if (this.options.length === 0) {
      this.optionsList.showNoResultsFound();
    }

    this.optionsList.updateVisibility(this.input.value);

  }

  handleBlurEvent() {

    this.onBlur();

  }

  handleValueSelect([option]) {

    setAttr(this.input, {
      value: option.label
    });

    this.onSelect(option);

  }

  async updateOptions(newValue) {

    this.optionsList.setWorking();

    const options = await this.optionsLoader(newValue);

    this.optionsList.setIdle();
    this.optionsList.setEntries(options);
    this.optionsList.updateVisibility(this.input.value);

    if (options.length === 0) {
      if (this.addNewOption === true) {
        this.optionsList.showAddNewOption();
      } else {
        this.optionsList.showNoResultsFound();
      }
    }

    this.notifyContentChanged();

    this.showOptions();

  }

  notifyContentChanged() {

    this.onContentChanged(this.el);

  }

  refreshOptions() {

    this.updateOptions(this.input.value);

  }

  handleTextInputChange(event) {

    const previousValue = this.input.previousValue;
    const newValue = this.input.value;

    if (newValue === previousValue) {
      return;
    }

    this.input.previousValue = newValue;
    this.optionsList.updateActions({ text: newValue });

    setTimeout(this.updateOptions.bind(this, newValue), 1);

  }

  handleTextInputKeyDownEvent(event) {

    const key = event.key;

    if (key === keys.ENTER) {
      this.optionsList.selectHighlightedOption();
    } else if (key === keys.ARROW_UP) {
      this.optionsList.highlightPreviousOption();
    } else if (key === keys.ARROW_DOWN) {
      this.optionsList.highlightNextOption();
    } else if (key === keys.ESCAPE) {
      event.stopPropagation();
      this.outsideClickHandler(event.target);
    }

  }

  setWorking() {

    this.optionsList.setWorking();

  }

  setIdle() {

    this.optionsList.setIdle();

  }

  setOptions(options) {

    this.options = options;
    this.optionsList.setEntries(this.options);
    this.optionsList.updateVisibility(this.input.value);

    if (this.options.length === 0) {
      this.optionsList.showNoResultsFound();
    }

  }

  focus() {

    this.input.focus();
    this.showOptions();

  }

  clear() {

    this.input.value = '';
    this.optionsList.unsetFilter();
    this.optionsList.updateActions({ text: '' });

  }

  clearAll() {

    this.clear();
    this.optionsList.clear();

  }

  setPlaceholder(placeholder) {

    setAttr(this.input, {
      placeholder
    });

  }

  setValue(option) {

    const selectedOptions = [];

    if (option.label !== '') {
      selectedOptions.push(option);
    }

    this.optionsList.setSelectedEntries(selectedOptions);
    this.input.value = option.label;

  }

  getValue() {

    const selectedEntries = this.optionsList.getSelectedEntries();

    if (selectedEntries.length === 0) {
      return null;
    }

    return selectedEntries[0];

  }

  outsideClickHandler(clickTargetElement) {

    this.optionsList.resetHighlighted();
    this.onClose(clickTargetElement);

  }

  registerOutsideEventHandler() {

    this.outsideEventRegistration = new OutsideClickEventRegistration(this.el, this.outsideClickHandler.bind(this));

  }

  unregisterOutsideEventHandler() {

    this.outsideEventRegistration.cancel();

  }

}

export default SingleValueSelector;